"use client";

import { LocalStorageKeys } from "@/configs";
import { type IPublisher, PublisherType } from "@/lib/services/types/publisher";
import React from "react";
import { AuthContext } from "./auth-provider";

export interface PublisherContextType {
  activePublisher: IPublisher | undefined;
  switchPublisher: (username: string) => void;
  isPublisherReady: boolean;
}

export const PublisherContext = React.createContext<PublisherContextType>({
  activePublisher: undefined,
  switchPublisher: () => {},
  isPublisherReady: false,
});

export function PublisherProvider({ children }: { children: React.ReactNode }) {
  const { authData } = React.useContext(AuthContext);
  const [activePublisherUsername, setActivePublisherUsername] = React.useState<string | null>(null);
  const [isPublisherReady, setIsPublisherReady] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setActivePublisherUsername(localStorage.getItem(LocalStorageKeys.activePublisherUsername));
      setIsPublisherReady(true);
    }
  }, []);

  const activePublisher = React.useMemo(() => {
    if (!authData?.publishers || authData.publishers.length === 0) return undefined;
    const blogPublishers = authData.publishers.filter((p) => !p.type || p.type === PublisherType.Blog);
    if (blogPublishers.length === 0) return undefined;
    const found = blogPublishers.find((p) => p.username === activePublisherUsername);
    return found || blogPublishers[0];
  }, [authData?.publishers, activePublisherUsername]);

  const switchPublisher = React.useCallback((username: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(LocalStorageKeys.activePublisherUsername, username);
      setActivePublisherUsername(username);
    }
  }, []);

  return <PublisherContext.Provider value={{ activePublisher, switchPublisher, isPublisherReady }}>{children}</PublisherContext.Provider>;
}
